/**
 * UsersTab
 *
 * Organism that renders the Business Account Users table and
 * the user-specific filter drawer. Column definitions and action
 * menus are self-contained; mutation callbacks are delegated to
 * the parent via `onEditUser` / `onDeleteUser`.
 *
 * ## Component Hierarchy
 * - **Level**: Organism
 * - **Feature**: Users
 *
 * @module features/users/components/ui/organisms/UsersTab
 */
import React, { useCallback, useMemo } from 'react';
import { Button, Dropdown, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { FormOutlined, MoreOutlined, StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { useLanguage } from '@/shared/hooks/useLanguage';
import { PAGE_SIZE_OPTIONS } from '@/features/users/constants/pagination';
import { getProfileI18nKey } from '@/features/users/utils/profileLabels';
import { getSkeletonTableProps } from '@/features/users/utils/tableSkeleton';
import { UserFiltersDrawer } from '@/features/users/components/ui/molecules/UserFiltersDrawer';
import { useExternalProfiles } from '@/features/users/hooks/useExternalProfiles';
import type { useUserTab } from '@/features/users/hooks/useUserTab';
import type { BusinessAccountUser } from '@/features/users/types/users.types';

import { TableEmptyState } from '@/features/users/components/ui/molecules/TableEmptyState';

import './UsersTab.css';

export interface UsersTabProps {
  /** All state and handlers coming from useUserTab */
  tab: ReturnType<typeof useUserTab>;
  /** Whether the current user can edit other users */
  canEdit: boolean;
  /** Whether the current user can delete other users */
  canDelete: boolean;
  /** Callback when requesting to edit a user's profile */
  onEditUser: (user: BusinessAccountUser) => void;
  /** Callback when requesting to delete a user */
  onDeleteUser: (user: BusinessAccountUser) => void;
}

/**
 * UsersTab Component
 *
 * @param props - Component properties
 * @returns Table with user rows and filter drawer
 *
 * @example
 * ```tsx
 * const tab = useUserTab();
 * <UsersTab
 *   tab={tab}
 *   canEdit={canEdit}
 *   canDelete={canDelete}
 *   onEditUser={handleEditProfile}
 *   onDeleteUser={handleDeleteUser}
 * />
 * ```
 */
export const UsersTab: React.FC<UsersTabProps> = ({
  tab,
  canEdit,
  canDelete,
  onEditUser,
  onDeleteUser,
}) => {
  const { t } = useLanguage();
  const { data: profiles } = useExternalProfiles();

  const getUserActionMenu = useCallback(
    (user: BusinessAccountUser): MenuProps['items'] => [
      {
        key: 'edit',
        icon: <FormOutlined />,
        label: t('Edit profile'),
        onClick: () => onEditUser(user),
      },
      ...(user.status !== 'INACTIVE' && canDelete
        ? [
            {
              key: 'delete',
              icon: <StopOutlined />,
              label: t('Exclude'),
              onClick: () => onDeleteUser(user),
            },
          ]
        : []),
    ],
    [t, onEditUser, onDeleteUser, canDelete]
  );

  const columns: ColumnsType<BusinessAccountUser> = useMemo(
    () => [
      {
        title: t('Email'),
        dataIndex: ['email', 'email'],
        key: 'email',
      },
      {
        title: t('Profile'),
        dataIndex: ['profile', 'name'],
        key: 'profile',
        render: (name: string) => {
          if (!name) {
            return (
              <span className="idf-profile-tag idf-profile-tag--warning">
                {t('No Profile')}
              </span>
            );
          }
          return (
            <span className="idf-profile-tag idf-profile-tag--default">
              {t(getProfileI18nKey(name))}
            </span>
          );
        },
      },
      {
        title: t('Date'),
        dataIndex: 'registrationDate',
        key: 'registrationDate',
        render: (value: string) =>
          value ? (
            <span className="idf-date-cell">
              <span className="idf-date-cell__date">
                {dayjs(value).format('DD/MM/YYYY')}
              </span>
              <span className="idf-date-cell__time">
                {dayjs(value).format('HH:mm')}
              </span>
            </span>
          ) : (
            '-'
          ),
      },
      ...(canEdit
        ? [
            {
              title: '',
              key: 'actions',
              align: 'right' as const,
              width: 60,
              render: (_: unknown, record: BusinessAccountUser) => (
                <Dropdown
                  menu={{ items: getUserActionMenu(record) }}
                  trigger={['click']}
                  placement="bottomRight"
                  overlayClassName="idf-user-actions-dropdown"
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    aria-label={t('User actions')}
                  />
                </Dropdown>
              ),
            },
          ]
        : []),
    ],
    [t, canEdit, getUserActionMenu]
  );

  // While (re)loading, render skeleton rows so the table looks refreshed.
  const isTableLoading = tab.usersLoading || tab.isRefreshing;
  const rows = tab.usersData?.content ?? [];
  const skeleton = getSkeletonTableProps<BusinessAccountUser>(
    columns,
    rows.length
  );

  return (
    <>
      <div className="idf-data-table">
        <Table<BusinessAccountUser>
          columns={isTableLoading ? skeleton.columns : columns}
          dataSource={isTableLoading ? skeleton.dataSource : rows}
          rowKey={isTableLoading ? skeleton.rowKey : 'id'}
          pagination={{
            current: tab.usersPage + 1,
            pageSize: tab.usersPageSize,
            total: tab.usersData?.page?.totalElements ?? 0,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS as unknown as string[],
            onChange: (nextPage, nextPageSize) => {
              tab.setUsersPage(nextPage - 1);
              if (nextPageSize) tab.setUsersPageSize(nextPageSize);
            },
          }}
          locale={{
            emptyText: (
              <TableEmptyState
                title={t('No users')}
                description={t('No users were found')}
              />
            ),
          }}
        />
      </div>

      <UserFiltersDrawer
        open={tab.filtersOpen}
        draftFilters={tab.draftFilters}
        profiles={profiles}
        onDraftChange={tab.setDraftFilters}
        onClose={tab.handleCloseFilters}
        onClear={tab.handleClearFilters}
        onApply={tab.handleApplyFilters}
      />
    </>
  );
};

export default UsersTab;
